import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import {
  DEFAULT_LANGUAGE,
  DEFAULT_NAMESPACE,
  STORAGE_KEY_LANGUAGE,
  SUPPORTED_LANGUAGES,
} from "./types";

// Import locale files statically for bundling
// (avoids needing an HTTP backend for initial load)
import enCore from "@/locales/en/core.json";
import enAuth from "@/locales/en/auth.json";
import enCommon from "@/locales/en/common.json";
import enDashboard from "@/locales/en/dashboard.json";
import enAccount from "@/locales/en/account.json";
import enSecurity from "@/locales/en/security.json";
import enSettings from "@/locales/en/settings.json";
import esCore from "@/locales/es/core.json";
import esAuth from "@/locales/es/auth.json";
import esCommon from "@/locales/es/common.json";
import esDashboard from "@/locales/es/dashboard.json";
import esAccount from "@/locales/es/account.json";
import esSecurity from "@/locales/es/security.json";
import esSettings from "@/locales/es/settings.json";
import frCore from "@/locales/fr/core.json";
import frAuth from "@/locales/fr/auth.json";
import frCommon from "@/locales/fr/common.json";
import frDashboard from "@/locales/fr/dashboard.json";
import frAccount from "@/locales/fr/account.json";
import frSecurity from "@/locales/fr/security.json";
import frSettings from "@/locales/fr/settings.json";

const resources = {
  en: {
    core: enCore,
    auth: enAuth,
    common: enCommon,
    dashboard: enDashboard,
    account: enAccount,
    security: enSecurity,
    settings: enSettings,
  },
  es: {
    core: esCore,
    auth: esAuth,
    common: esCommon,
    dashboard: esDashboard,
    account: esAccount,
    security: esSecurity,
    settings: esSettings,
  },
  fr: {
    core: frCore,
    auth: frAuth,
    common: frCommon,
    dashboard: frDashboard,
    account: frAccount,
    security: frSecurity,
    settings: frSettings,
  },
};

function detectLanguage(): string {
  const supportedCodes: Set<string> = new Set(SUPPORTED_LANGUAGES.map((l) => l.code));

  if (typeof window !== "undefined") {
    // 1. URL parameter
    const urlParams = new URLSearchParams(window.location.search);
    const langParam = urlParams.get("lang");
    if (langParam && supportedCodes.has(langParam)) {
      return langParam;
    }

    // 2. localStorage
    const stored = localStorage.getItem(STORAGE_KEY_LANGUAGE);
    if (stored && supportedCodes.has(stored)) {
      return stored;
    }

    // 3. Browser language
    if (typeof navigator !== "undefined") {
      const browserLang = navigator.language.split("-")[0];
      if (supportedCodes.has(browserLang)) {
        return browserLang;
      }
    }
  }

  // 4. Default
  return DEFAULT_LANGUAGE;
}

function updateDocumentDirection(language: string) {
  const langInfo = SUPPORTED_LANGUAGES.find((l) => l.code === language);
  if (langInfo && typeof document !== "undefined") {
    document.documentElement.dir = langInfo.dir;
    document.documentElement.lang = language;
  }
}

const i18nInstance = i18n.createInstance();

i18nInstance.use(initReactI18next).init({
  resources,
  lng: detectLanguage(),
  fallbackLng: DEFAULT_LANGUAGE,
  defaultNS: DEFAULT_NAMESPACE,
  ns: ["core", "auth", "common", "dashboard", "account", "security", "settings"],
  interpolation: {
    escapeValue: false, // React handles escaping
  },
  react: {
    useSuspense: false,
  },
});

// Set initial document direction
updateDocumentDirection(i18nInstance.language);

// Update document direction on language change
i18nInstance.on("languageChanged", (lng) => {
  updateDocumentDirection(lng);
  if (typeof window !== "undefined") {
    localStorage.setItem(STORAGE_KEY_LANGUAGE, lng);
  }
});

/**
 * Track which plugin namespaces have already been registered
 * to prevent duplicate registrations.
 */
const registeredPluginNamespaces = new Set<string>();

/**
 * Register a plugin i18n namespace with resources for a specific language.
 *
 * Plugin i18n is lazy-loaded: this function is called when the plugin
 * is first rendered, not at application initialization.
 *
 * @param ns - The namespace name (e.g., "plugin-notes")
 * @param lang - The language code (e.g., "en")
 * @param resources - Translation key-value pairs
 */
export function addPluginNamespace(
  ns: string,
  lang: string,
  resources: Record<string, unknown>,
): void {
  const nsKey = `${lang}:${ns}`;
  if (registeredPluginNamespaces.has(nsKey)) return;

  i18nInstance.addResourceBundle(lang, ns, resources, true, true);
  registeredPluginNamespaces.add(nsKey);
}

export { i18nInstance, updateDocumentDirection };
export default i18nInstance;
